import { Component, Input, OnInit } from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { Observable } from 'rxjs';
import { ModelUtil } from 'src/app/model/model-util';
import { IQualification } from 'src/app/model/qualifications/iqualification';
import { IQualificationBasis } from 'src/app/model/qualifications/iqualification-basis';
import { IQualificationBasisDef } from 'src/app/model/view/iqualification-basis-def';
import { APPLICATION_NAMES, QUALIFICATION_NAMES } from 'src/app/service/ansokan-form-builder/application-names';
import { QUALIFICATION_FORM_DEF } from 'src/app/service/ansokan-form-builder/form-definitions';
import { AnsokanFormGroupService } from 'src/app/service/ansokan-form-group/ansokan-form-group.service';
import { FormUtil } from 'src/app/util/form-util';
import { INextStepDef, IQualificationDef, IStepCategoryDef, IStepDataDef } from '../../model/view/iqualification-def';

@Component({
    selector: 'app-qualifications',
    styleUrls: ['./qualifications.component.scss'],
    templateUrl: './qualifications.component.html',
    standalone: false
})
export class QualificationsComponent implements OnInit {

  @Input() public availableQualifications: IQualificationDef[];
  @Input() public pageGroup: UntypedFormGroup;
  @Input() public firstLabel: string;
  public selectedQualificationControl: UntypedFormControl;
  public nextStepControl: UntypedFormControl;
  public nextStepData$: Observable<IStepDataDef[]>;
  public nextToNextStepControl: UntypedFormControl;
  public nextToNextStepData$: Observable<IStepDataDef[]>;
  public validationGroup: UntypedFormGroup;
  private currentQualification: UntypedFormGroup;
  private category: string;
  private dateOfBirth: Date;
  showWarning: boolean = false;

  constructor(
    private groupService: AnsokanFormGroupService, private fb: UntypedFormBuilder) { }

  public get qualifications(): UntypedFormArray {
    return this.pageGroup ? this.pageGroup.get(QUALIFICATION_NAMES.KEY) as UntypedFormArray : null;
  }

  public get basisDef(): IQualificationBasisDef[] {
    const selected: IQualificationDef = this.selectedQualification;
    const orientation = this.nextToNextStepControl.value;
    return selected && this.category ? selected.availableBases(this.category, this.dateOfBirth, orientation) : null;
  }

  public get selectedQualification(): IQualificationDef {
    const code: string = this.selectedQualificationControl.value;
    return this.availableQualifications ?
      this.availableQualifications.filter((def: IQualificationDef) => def.code === code)[0] : null;
  }

  public get nextStep(): INextStepDef {
    const selected: IQualificationDef = this.selectedQualification;
    return selected ? selected.next : null;
  }

  public get nextToNextStep(): INextStepDef {
    const next: INextStepDef = this.nextStep;
    return next ? next.next : null;
  }

  public get basisArray(): UntypedFormArray {
    return this.currentQualification ? this.currentQualification.get(QUALIFICATION_NAMES.BASES.KEY) as UntypedFormArray : null;
  }

  public ngOnInit() {
    // Get the applicant date of birth.
    const ssn: string = this.groupService.get(APPLICATION_NAMES.APPLICANT_SSN).value;
    this.dateOfBirth = ssn ? ModelUtil.getDateOfBirth(ssn) : null;
    // Create selected qualification control.
    this.selectedQualificationControl = new UntypedFormControl(null, Validators.required);
    this.selectedQualificationControl.valueChanges.subscribe((code: string) => this.onQualificationChange(code));
    // Create next step controls.
    this.nextStepControl = new UntypedFormControl(null, Validators.required);
    this.nextStepControl.valueChanges.subscribe((code: string) => this.onNextChange(code));
    this.nextStepControl.disable({ emitEvent: false });
    this.nextToNextStepControl = new UntypedFormControl(null, Validators.required);
    this.nextToNextStepControl.valueChanges.subscribe((code: string) => this.onNextToNextChange(code));
    this.nextToNextStepControl.disable({ emitEvent: false });
    // Create view form group.
    this.currentQualification = this.fb.group(QUALIFICATION_FORM_DEF());
    // Create validation group.
    this.validationGroup = this.fb.group({
      bases: this.basisArray,
      first: this.selectedQualificationControl,
      second: this.nextStepControl,
      third: this.nextToNextStepControl,
    });
  }

  public setCategory(code: string, subjects: IStepDataDef[]): void {
    const subject = subjects.find((s) => s.code === code);
    this.category = subject && subject.categories && subject.categories.length > 0 ? subject.categories[0].code : 'none';
  }

  public addQualification(): void {
    const qualification = this.getQualification();
    if (!qualification) {
      return;
    };
    const array = this.pageGroup.get(QUALIFICATION_NAMES.KEY) as UntypedFormArray;
    // Filter out selected bases that is not available.
    this.removeUnavailableBases(qualification);
    if (!this.qualificationExists(qualification.value)) {
      // Add qualification.
      array.push(qualification);
    } else {
      const qualificationIndex = this.indexOfQualification(qualification.value);
      // Add bases.

      const savedBases = array.at(qualificationIndex).get(QUALIFICATION_NAMES.BASES.KEY) as UntypedFormArray;
      const addedBases = qualification.get(QUALIFICATION_NAMES.BASES.KEY) as UntypedFormArray;
      this.mergeBases(savedBases, addedBases);
    }
    // Reset controls.
    this.resetCurrentQualification();
  }

  private removeUnavailableBases(qualification: UntypedFormGroup): void {
    const basesFormArray = qualification.get(QUALIFICATION_NAMES.BASES.KEY) as UntypedFormArray;
    const basesToRemove: IQualificationBasis[] =
      basesFormArray.value.filter((basis) => !this.basisDef.find((b) => b.typeCode === basis.typeCode));
    basesToRemove.forEach((basis) => {
      const bases: IQualificationBasis[] = basesFormArray.value;
      const index = bases.findIndex((b) => b.typeCode === basis.typeCode);
      basesFormArray.removeAt(index);
    });
  }

  private mergeBases(saved: UntypedFormArray, added: UntypedFormArray): void {
    const savedBases: IQualificationBasis[] = saved.value;
    const addedBases: IQualificationBasis[] = added.value as IQualificationBasis[];
    // Merge added bases with saved bases.
    addedBases.forEach((basis, i) => {
      const savedBasisIndex = savedBases.findIndex((b) => b.typeCode === basis.typeCode);
      if (savedBasisIndex === -1) {
        // If basis not already exists.
        saved.push(added.at(i));
      } else if (basis.yearIntervals && savedBases[savedBasisIndex].yearIntervals) {
        // If basis exists, merge intervals.
        const intervals = saved.at(savedBasisIndex).get(QUALIFICATION_NAMES.BASES.YEAR_INTERVALS_OPTIONAL) as UntypedFormArray;
        basis.yearIntervals
          .filter((interval) => savedBases[savedBasisIndex].yearIntervals.indexOf(interval) === -1)
          .forEach((interval) => intervals.push(this.fb.control(interval)));
      } else if (basis.fromYear && savedBases[savedBasisIndex].fromYear) {
        // if basis exists and has free intervals, update from and to.
        saved.at(savedBasisIndex).get(QUALIFICATION_NAMES.BASES.YEAR_FROM_OPTIONAL).setValue(basis.fromYear);
        saved.at(savedBasisIndex).get(QUALIFICATION_NAMES.BASES.YEAR_TO_OPTIONAL).setValue(basis.toYear);
      }
    });
  }

  private qualificationExists(qualification: IQualification): boolean {
    return this.indexOfQualification(qualification) !== -1;
  }

  private indexOfQualification(qualification: IQualification): number {
    const quals = this.pageGroup.get(QUALIFICATION_NAMES.KEY) as UntypedFormArray;
    const values: IQualification[] = quals.value;
    return values.findIndex((current) => current.typeCode === qualification.typeCode
      && current.subjectCode === qualification.subjectCode
      && current.typeOfSchoolingCode === qualification.typeOfSchoolingCode
      && current.studyPathCode === qualification.studyPathCode
      && current.orientationCode === qualification.orientationCode
      && current.languageCode === qualification.languageCode);
  }

  private indexOfCategory(categories: IStepCategoryDef[], code: string): number {
    return categories.findIndex((current) => current.code === code);
  }

  private onQualificationChange(code: string): void {
    // Reset next step.
    this.resetNextStep();
    // Load next step data.
    const next = this.nextStep;
    this.nextStepData$ = next ? next.data(code) : null;
    this.category = next ? null : 'none';
    // Load next to next data.
    const nextToNext = this.nextToNextStep;
    this.nextToNextStepData$ = nextToNext ? nextToNext.data(code) : null;
    // Reset current group values.
    this.currentQualification.reset();
    this.basisArray.clear();
    // Set qualification values.
    this.currentQualification.get(QUALIFICATION_NAMES.TYPE_CODE).setValue(code);
    this.currentQualification.get(QUALIFICATION_NAMES.TYPE_NAME).setValue(this.selectedQualification.name);
    // Update validation.
    this.updateValidationGroup();
  }

  private onNextChange(code: string): void {
    // Set page group values.
    this.setFormGroupData(code, this.nextStep.name, this.nextStepData$);
    // Update validity.
    this.validationGroup.updateValueAndValidity();
  }

  private onNextToNextChange(code: string): void {
    // Set page group values.
    this.setFormGroupData(code, this.nextToNextStep.name, this.nextToNextStepData$);
    // Update validity.
    this.validationGroup.updateValueAndValidity();
  }

  private setFormGroupData(code: string, name: string, data: Observable<IStepDataDef[]>): void {
    data.subscribe((value: IStepDataDef[]) => {
      const stepData = value.filter((def: IStepDataDef) => def.code === code)[0];
      if (stepData) {
        this.currentQualification.get(name + 'Code').setValue(stepData.code);
        this.currentQualification.get(name + 'Name').setValue(stepData.name);
      }
    });
  }

  private resetCurrentQualification(): void {
    this.currentQualification.reset();
    this.currentQualification.get(QUALIFICATION_NAMES.BASES.KEY).reset();
    this.selectedQualificationControl.reset(null, { emitEvent: false });
    this.resetNextStep();
    this.basisArray.reset();
  }

  private resetNextStep(): void {
    this.nextStepControl.reset(null, { emitEvent: false });
    this.nextStepData$ = null;
    this.nextToNextStepData$ = null;
    this.nextToNextStepControl.reset(null, { emitEvent: false });
    this.category = null;
  }

  private updateValidationGroup(): void {
    // Enable or disable controls.
    if (this.nextStep) {
      this.nextStepControl.enable({ emitEvent: false });
    } else {
      this.nextStepControl.disable({ emitEvent: false });
    }
    if (this.nextToNextStep) {
      this.nextToNextStepControl.enable({ emitEvent: false });
    } else {
      this.nextToNextStepControl.disable({ emitEvent: false });
    }
  }

  private getQualification(): UntypedFormGroup {

    let ret = FormUtil.clone<UntypedFormGroup>(this.currentQualification);
   
    if(this.nextStepData$ && !ret.value.subjectName){
      this.showWarning = true;
      return null;
    }
    this.showWarning = false;
    return ret;
  }
}
